import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Trash2, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

export default function AdminNewsletter() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscribers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/newsletter`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const handleDelete = async (subscriberId) => {
    if (!window.confirm('Es-tu sûr de vouloir supprimer cet abonné ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/newsletter/${subscriberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Abonné supprimé');
      fetchSubscribers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExport = () => {
    const csv = 'Email,Date d\'inscription\n' + 
      subscribers.map(s => `${s.email},${new Date(s.subscribed_at).toLocaleDateString('fr-FR')}`).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newsletter-subscribers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Export téléchargé');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="admin-newsletter">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-rm-text">Newsletter</h1>
          <p className="text-rm-text-secondary">Gère tes abonnés à la newsletter</p>
        </div>
        {subscribers.length > 0 && (
          <Button 
            onClick={handleExport}
            variant="outline"
            className="rounded-xl"
          >
            <Download className="w-4 h-4 mr-2" />
            Exporter CSV
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Abonnés ({subscribers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="spinner"></div>
            </div>
          ) : subscribers.length === 0 ? (
            <p className="text-rm-text-secondary text-center py-10">
              Aucun abonné pour le moment
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 text-sm font-medium text-rm-text-secondary">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-rm-text-secondary">Date d'inscription</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-rm-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((subscriber, index) => (
                    <motion.tr
                      key={subscriber.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="border-b last:border-0 hover:bg-slate-50"
                    >
                      <td className="py-3 px-4 text-rm-text">{subscriber.email}</td>
                      <td className="py-3 px-4 text-rm-text-secondary">{formatDate(subscriber.subscribed_at)}</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => handleDelete(subscriber.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
